import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Stack,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  setApplicationStep,
  setUkCoolingOff,
  setUkFinalConfirmation,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const COOLING_OFF_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

export default function UKCoolingOffPending() {
  const dispatch = useDispatch();
  const { ukOnboarding } = useOnboarding();
  const { coolingOff } = ukOnboarding;

  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [isDeclined, setIsDeclined] = useState(ukOnboarding.finalConfirmation === "declined");

  const [getCoolingOffStatus] = api.useLazyGetCoolingOffStatusQuery();
  const [declineAccount, { isLoading: isDeclining }] = api.useDeclineAccountMutation();

  useEffect(() => {
    initializeCoolingOff();
  }, []);

  useEffect(() => {
    if (coolingOff.endsAt) {
      const timer = setInterval(() => {
        const now = Date.now();
        const remaining = coolingOff.endsAt! - now;

        if (remaining <= 0) {
          setIsComplete(true);
          setTimeRemaining(0);
          clearInterval(timer);
          dispatch(
            setUkCoolingOff({
              status: "completed",
            }),
          );
        } else {
          setTimeRemaining(remaining);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [coolingOff.endsAt]);

  const initializeCoolingOff = async () => {
    try {
      // Fetch the cooling off status - it should already be started when assessment was passed
      const status = await getCoolingOffStatus().unwrap();

      if (status.hasCompleted) {
        setIsComplete(true);
        dispatch(
          setUkCoolingOff({
            status: "completed",
            startedAt: status.startedAt,
            endsAt: status.endsAt,
          }),
        );
        return;
      }

      if (status.hasStarted) {
        dispatch(
          setUkCoolingOff({
            status: "in_progress",
            startedAt: status.startedAt,
            endsAt: status.endsAt,
          }),
        );
        return;
      }

      // If neither in progress nor complete, something went wrong
      setError("Cooling-off period has not been started. Please contact support.");
    } catch (err: any) {
      setError(err?.data?.message || "Failed to load cooling-off status.");
    }
  };

  const formatTimeRemaining = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const progress = timeRemaining !== null
    ? ((COOLING_OFF_DURATION_MS - timeRemaining) / COOLING_OFF_DURATION_MS) * 100
    : 0;

  const handleContinue = () => {
    dispatch(setApplicationStep("ukFinalConfirmation"));
  };

  const handleDecline = async () => {
    try {
      await declineAccount().unwrap();
      dispatch(setUkFinalConfirmation("declined"));
      setIsDeclined(true);
      setShowDeclineDialog(false);
    } catch (err: any) {
      setError(err?.data?.message || "Failed to process your request. Please try again.");
      setShowDeclineDialog(false);
    }
  };

  if (isDeclined) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper
            elevation={1}
            sx={{
              p: 4,
              borderRadius: 2,
              textAlign: "center",
              backgroundColor: "#ECEFF1",
              border: "1px solid #B0BEC5",
            }}
          >
            <Typography variant="h4" fontWeight={600} gutterBottom color="#546E7A">
              Application Closed
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your application has been closed as requested. Thank you for considering Caleb
              & Brown.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Changed Your Mind?
            </Typography>
            <Typography variant="body1" paragraph>
              If you decide you would like to invest in cryptoassets in the future, you are
              welcome to start a new application at any time.
            </Typography>
            <Typography variant="body1">
              We're here to help if you have any questions about cryptoasset investment.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="center" mt={2}>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              onClick={() => (window.location.href = "/")}
              sx={{ minWidth: 200 }}
            >
              Return to Home
            </Button>
          </Stack>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isComplete) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper
            elevation={1}
            sx={{
              p: 4,
              borderRadius: 2,
              textAlign: "center",
              backgroundColor: "#E8F5E9",
              border: "2px solid #4CAF50",
            }}
          >
            <Typography variant="h4" fontWeight={600} gutterBottom color="#2E7D32">
              Cooling-Off Period Complete
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You can now proceed to confirm or decline your account activation.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleContinue}
              sx={{ minWidth: 300 }}
            >
              Continue to Final Confirmation
            </Button>
          </Stack>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} mt={2}>
      <Grid item xs={12}>
        <Paper
          elevation={1}
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: "center",
            backgroundColor: "#E3F2FD",
            border: "1px solid #90CAF9",
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 60, color: "#1976d2", mb: 2 }} />
          <Typography variant="h4" fontWeight={600} gutterBottom>
            24-Hour Cooling-Off Period
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            As required by UK regulations, there is a mandatory 24-hour cooling-off period
            before your account can be fully activated.
          </Typography>

          {timeRemaining !== null && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h2" fontWeight={700} color="primary" gutterBottom>
                {formatTimeRemaining(timeRemaining)}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Time Remaining
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 10, borderRadius: 5, mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}% Complete
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <InfoOutlinedIcon sx={{ color: "#1976d2", mt: 0.5 }} />
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Why is there a cooling-off period?
              </Typography>
              <Stack spacing={2}>
                <Typography variant="body1">
                  The cooling-off period is designed to protect you by giving you time to:
                </Typography>
                <Stack spacing={1} ml={2}>
                  <Typography variant="body2">
                    • Reflect on your decision to invest in cryptoassets
                  </Typography>
                  <Typography variant="body2">
                    • Consider the risks involved with cryptoasset investments
                  </Typography>
                  <Typography variant="body2">
                    • Ensure you are not acting impulsively
                  </Typography>
                  <Typography variant="body2">
                    • Review the information provided during the application process
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            What happens next?
          </Typography>
          <Stack spacing={2}>
            <Typography variant="body1">
              1. After the cooling-off period ends, you will receive a notification (via email
              and in-platform alert).
            </Typography>
            <Typography variant="body1">
              2. You will have the option to either <strong>confirm</strong> your account
              activation or <strong>decline</strong> to proceed.
            </Typography>
            <Typography variant="body1">
              3. Both options will be presented with equal prominence - there is no pressure
              to proceed if you have changed your mind.
            </Typography>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Can I leave this page?</strong>
            <br />
            Yes, you can safely close this page. The cooling-off timer will continue in the
            background. You will be notified when the period is complete, and you can return
            to complete your application at that time.
          </Typography>
        </Alert>
      </Grid>

      <Grid item xs={12} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Stack direction="row" justifyContent="center" mt={2}>
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            onClick={() => setShowDeclineDialog(true)}
          >
            I No Longer Want to Proceed
          </Button>
        </Stack>
      </Grid>

      {/* Decline Confirmation Dialog */}
      <Dialog open={showDeclineDialog} onClose={() => setShowDeclineDialog(false)}>
        <DialogTitle>Close Your Application</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to close your application?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Your application will be cancelled and you will not be able to trade cryptoassets
            through this account. You can start a new application at any time in the future if
            you change your mind.
          </Typography>
          <Alert severity="info">
            There is no penalty for declining. This is a valid choice and we respect your
            decision.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeclineDialog(false)} color="inherit">
            Go Back
          </Button>
          <Button onClick={handleDecline} variant="outlined" disabled={isDeclining}>
            {isDeclining ? "Processing..." : "Close Application"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
